import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import { toast } from 'react-toastify';
import config from '../config';
import ImageCropperModal from './common/ImageCropperModal';

const VenueBlogsManager = ({ venueId, isOwner = false }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    
    const [blogs, setBlogs] = useState([]);
    const [limits, setLimits] = useState({ max: 0, current: 0 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    
    // New blog form
    const [showAddForm, setShowAddForm] = useState(false);
    const [newBlog, setNewBlog] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        featured_image: '',
        status: 'draft'
    });

    // Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        fixedAspectRatio: 3/2, // 3:2 for Feed/Blog
        initialAspectRatio: 3/2,
        minWidth: 750
    });

    // Handle file selection (opens cropper)
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
             toast.error('Only JPG, PNG, and WebP are allowed');
             return;
        }

        // Check file size (10MB source limit for cropper)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }
        
        // Create object URL for cropper
        const objectUrl = URL.createObjectURL(file);
        
        setCropperState({
            show: true,
            imageSrc: objectUrl,
            fixedAspectRatio: 3/2,
            initialAspectRatio: 3/2,
            minWidth: 750
        });
        
        // Reset input
        e.target.value = '';
    };

    const handleCropperHide = () => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        if (editingBlog) {
            setEditingBlog(prev => ({
                ...prev,
                featured_image_file: croppedFile,
                featured_image_preview: previewUrl
            }));
        } else {
            setNewBlog(prev => ({
                ...prev,
                featured_image_file: croppedFile,
                featured_image_preview: previewUrl
            }));
        }
        
        // Cleanup cropper source
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    // Fetch blogs
    const fetchBlogs = useCallback(async () => {
        try {
            const endpoint = isOwner 
                ? `${config.API_BASE_URL}/api/venues/${venueId}/blogs`
                : `${config.API_BASE_URL}/api/public/venues/${venueId}/blogs`;

            const response = await auth.fetch(endpoint);
            const data = await response.json();
            
            if (response.ok) {
                // Backend now returns { blogs: [], limit: N }
                // Handle both new format and potential legacy format (just array)
                let blogList = Array.isArray(data) ? data : (data.blogs || []);
                const limitValue = !Array.isArray(data) && data.limit !== undefined ? data.limit : 0;
                
                // Normalize data
                blogList = blogList.map(blog => ({
                    ...blog,
                    image_url: blog.image_url || blog.featured_image, // Handle both
                    status: blog.status || 'published' // Default to published for public view
                }));

                setBlogs(blogList);
                setLimits({
                    max: limitValue,
                    current: blogList.length
                });
            }
        } catch (error) {
            console.error('Error fetching blogs:', error);
        } finally {
            setLoading(false);
        }
    }, [auth, venueId, isOwner]);

    // Fetch limits separately if not in blog response
    const fetchLimits = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/limits?venue_id=${venueId}`);
            const data = await response.json();
            
            if (response.ok && data.limits?.blogs) {
                setLimits({
                    max: data.limits.blogs.max,
                    current: data.limits.blogs.current
                });
            }
        } catch (error) {
            console.error('Error fetching limits:', error);
        }
    }, [auth, venueId]);

    useEffect(() => {
        if (venueId) {
            fetchBlogs();
            fetchLimits();
        }
    }, [venueId, fetchBlogs, fetchLimits]);

    // Get remaining slots
    const getRemainingSlots = () => {
        return Math.max(0, limits.max - limits.current);
    };

    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    // Generate slug from title
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    // Handle input change for new blog
    const handleNewBlogChange = (e) => {
        const { name, value } = e.target;
        setNewBlog(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'title') {
                updated.slug = generateSlug(value);
            }
            return updated;
        });
    };

    // Handle input change for editing blog
    const handleEditBlogChange = (e) => {
        const { name, value } = e.target;
        setEditingBlog(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'title') {
                updated.slug = generateSlug(value);
            }
            return updated;
        });
    };

    // Add new blog
    const handleAddBlog = async (e) => {
        e.preventDefault();
        
        if (!newBlog.title.trim()) {
            toast.error('Blog title is required');
            return;
        }
        
        if (getRemainingSlots() <= 0) {
            toast.error('You have reached the maximum number of blogs allowed');
            return;
        }
        
        setSaving(true);
        
        try {
            const formData = new FormData();
            formData.append('title', newBlog.title);
            formData.append('slug', newBlog.slug);
            formData.append('excerpt', newBlog.excerpt);
            formData.append('content', newBlog.content);
            formData.append('status', newBlog.status);
            
            if (newBlog.featured_image_file) {
                formData.append('featured_image', newBlog.featured_image_file);
            } else if (newBlog.featured_image) {
                // If user somehow entered a URL manually (if we kept the input as fallback, but we'll remove it)
                // But since we are changing input type to file, this might not be needed unless we support both.
                // For now, let's support it if it's there.
                formData.append('featured_image', newBlog.featured_image);
            }

            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/blogs`, {
                method: 'POST',
                body: formData
            });
            
            const data = await response.json();
            
            if (response.ok) {
                toast.success('Blog post created successfully');
                setNewBlog({
                    title: '',
                    slug: '',
                    excerpt: '',
                    content: '',
                    featured_image: '',
                    status: 'draft'
                });
                setShowAddForm(false);
                fetchBlogs();
                fetchLimits();
            } else {
                if (data.error === 'SUBSCRIPTION_LIMIT_EXCEEDED') {
                    toast.error(data.message);
                } else {
                    toast.error(data.message || 'Failed to create blog');
                }
            }
        } catch (error) {
            console.error('Error creating blog:', error);
            toast.error('Failed to create blog');
        } finally {
            setSaving(false);
        }
    };

    // Update blog
    const handleUpdateBlog = async (e) => {
        e.preventDefault();
        
        if (!editingBlog.title.trim()) {
            toast.error('Blog title is required');
            return;
        }
        
        setSaving(true);
        
        try {
            const formData = new FormData();
            formData.append('title', editingBlog.title);
            formData.append('slug', editingBlog.slug);
            formData.append('excerpt', editingBlog.excerpt);
            formData.append('content', editingBlog.content);
            formData.append('status', editingBlog.status);
            
            if (editingBlog.featured_image_file) {
                formData.append('featured_image', editingBlog.featured_image_file);
            } else if (editingBlog.featured_image) {
                formData.append('featured_image', editingBlog.featured_image);
            }

            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/blogs/${editingBlog.id}`, {
                method: 'PUT',
                body: formData
            });
            
            if (response.ok) {
                toast.success('Blog updated successfully');
                setEditingBlog(null);
                fetchBlogs();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update blog');
            }
        } catch (error) {
            console.error('Error updating blog:', error);
            toast.error('Failed to update blog');
        } finally {
            setSaving(false);
        }
    };

    // Delete blog
    const handleDeleteBlog = async (blogId) => {
        if (!window.confirm('Are you sure you want to delete this blog post?')) return;
        
        setDeletingId(blogId);
        
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/blogs/${blogId}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                toast.success('Blog deleted successfully');
                fetchBlogs();
                fetchLimits();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete blog');
            }
        } catch (error) {
            console.error('Error deleting blog:', error);
            toast.error('Failed to delete blog');
        } finally {
            setDeletingId(null);
        }
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        switch (status) {
            case 'published':
                return 'bg-success-100 text-success-600';
            case 'draft':
                return 'bg-warning-100 text-warning-600';
            case 'archived':
                return 'bg-secondary-100 text-secondary-600';
            default:
                return 'bg-neutral-100 text-neutral-600';
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-48">
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    return (
        <div className="card radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-12">
                    <h6 className="text-lg fw-semibold mb-0">
                        <Icon icon="mdi:post-outline" className="me-8" />
                        Venue Blog Posts
                    </h6>
                {isOwner && (
                    <div className="d-flex align-items-center gap-12">
                        <span className={`badge ${getRemainingSlots() > 0 ? 'bg-success-100 text-success-600' : limits.max === 0 ? 'bg-secondary-100 text-secondary-600' : 'bg-danger-100 text-danger-600'} px-12 py-6`}>
                            {limits.current} / {limits.max} posts used
                        </span>
                        {getRemainingSlots() > 0 && !showAddForm && !editingBlog && (
                            <button
                                className="btn btn-primary btn-sm radius-8"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="mdi:plus" className="me-4" />
                                New Post
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
        
        <div className="card-body p-24">
            {/* Limit Exceeded/Not Available Alert */}
            {isOwner && limits.max === 0 ? (
                <div className="alert alert-secondary mb-24 bg-neutral-100 border-neutral-200 text-neutral-600">
                    <div className="d-flex align-items-center">
                        <Icon icon="mdi:lock" className="text-xl me-12" />
                        <div>
                            <p className="fw-semibold mb-0">Blog Feature Not Available</p>
                            <p className="text-sm mb-0">
                                Your current plan does not include blog posts. 
                                <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent ms-4 text-primary-600 fw-medium">Upgrade to add this feature</button>
                            </p>
                        </div>
                    </div>
                </div>
            ) : isOwner && getRemainingSlots() <= 0 && !showAddForm && !editingBlog && (
                    <div className="alert alert-warning mb-24 d-flex align-items-center">
                    <Icon icon="mdi:alert" className="me-12 text-xl" />
                    <div>
                        You have reached the maximum number of blog posts ({limits.max}) allowed by your subscription plan.
                        <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent ms-8 fw-semibold text-decoration-underline">Upgrade your plan</button>
                    </div>
                </div>
            )}

                {/* Add/Edit Blog Form */}
                {(showAddForm || editingBlog) && (
                    <form 
                        onSubmit={editingBlog ? handleUpdateBlog : handleAddBlog} 
                        className="bg-neutral-50 radius-12 p-24 mb-24"
                    >
                        <h6 className="text-md fw-semibold mb-16">
                            {editingBlog ? 'Edit Blog Post' : 'Create New Post'}
                        </h6>
                        
                        <div className="row g-16">
                            <div className="col-md-8">
                                <label className="form-label text-sm fw-medium">
                                    Title <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    name="title"
                                    value={editingBlog ? editingBlog.title : newBlog.title}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="Blog post title"
                                    maxLength={200}
                                />
                            </div>
                            
                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">Status</label>
                                <select
                                    className="form-select radius-8"
                                    name="status"
                                    value={editingBlog ? editingBlog.status : newBlog.status}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                            
                            <div className="col-md-8">
                                <label className="form-label text-sm fw-medium">Slug</label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    name="slug"
                                    value={editingBlog ? editingBlog.slug : newBlog.slug}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="blog-post-url-slug"
                                />
                                <small className="text-secondary-light">URL-friendly version of the title</small>
                            </div>
                            
                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">Featured Image</label>
                                <input
                                    type="file"
                                    className="form-control radius-8"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                <small className="text-secondary-light">Max 2MB. Leave empty to keep current image.</small>
                                {(editingBlog?.featured_image || editingBlog?.featured_image_preview || newBlog.featured_image_preview) && (
                                    <div className="mt-2">
                                        <img 
                                            src={editingBlog?.featured_image_preview || newBlog.featured_image_preview || (editingBlog?.featured_image?.startsWith('http') ? editingBlog.featured_image : `${config.API_BASE_URL}/${editingBlog.featured_image}`)} 
                                            alt="Preview" 
                                            className="img-thumbnail"
                                            style={{ height: '100px', objectFit: 'cover' }}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div className="col-12">
                                <label className="form-label text-sm fw-medium">Excerpt</label>
                                <textarea
                                    className="form-control radius-8"
                                    name="excerpt"
                                    value={editingBlog ? editingBlog.excerpt : newBlog.excerpt}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="Short description for previews"
                                    rows={2}
                                    maxLength={500}
                                />
                            </div>
                            
                            <div className="col-12">
                                <label className="form-label text-sm fw-medium">Content</label>
                                <textarea
                                    className="form-control radius-8"
                                    name="content"
                                    value={editingBlog ? editingBlog.content : newBlog.content}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="Full blog post content..."
                                    rows={8}
                                />
                            </div>
                            
                            <div className="col-12">
                                <div className="d-flex gap-12 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary radius-8 px-16"
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setEditingBlog(null);
                                            setNewBlog({
                                                title: '',
                                                slug: '',
                                                excerpt: '',
                                                content: '',
                                                featured_image: '',
                                                status: 'draft'
                                            });
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary radius-8 px-24"
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-8" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="mdi:content-save" className="me-8" />
                                                {editingBlog ? 'Update Post' : 'Create Post'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
                
                {/* Blog Posts List */}
                {blogs.length > 0 ? (
                    <div className="row g-16">
                        {blogs.map((blog) => (
                            <div key={blog.id} className="col-md-6 col-lg-4">
                                <div className="card h-100 border radius-8 hover-shadow-sm">
                                    {blog.image_url && (
                                        <img
                                            src={blog.image_url.startsWith('http') ? blog.image_url : `${config.API_BASE_URL}/${blog.image_url}`}
                                            alt={blog.title}
                                            className="card-img-top object-fit-cover"
                                            style={{ height: '140px' }}
                                        />
                                    )}
                                    <div className="card-body p-16">
                                        <div className="d-flex justify-content-between align-items-start mb-8">
                                            <span className={`badge ${getStatusBadge(blog.status)} px-8 py-4`}>
                                                {blog.status}
                                            </span>
                                            <small className="text-secondary-light">
                                                {new Date(blog.created_at).toLocaleDateString()}
                                            </small>
                                        </div>
                                        <h6 className="text-md fw-semibold mb-8 line-clamp-2">
                                            {blog.title}
                                        </h6>
                                        {blog.excerpt && (
                                            <p className="text-secondary-light text-sm mb-12 line-clamp-2">
                                                {blog.excerpt}
                                            </p>
                                        )}
                                        {blog.slug && (
                                            <p className="text-xs text-info-600 mb-12">
                                                /{blog.slug}
                                            </p>
                                        )}
                                    </div>
                                    <div className="card-footer border-top p-12 bg-transparent">
                                        <div className="d-flex gap-8">
                                            {isOwner && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm radius-8 flex-grow-1 d-flex align-items-center justify-content-center"
                                                        onClick={() => setEditingBlog({ ...blog, featured_image: blog.image_url })}
                                                    >
                                                        <Icon icon="mdi:pencil" className="me-4 text-lg" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="w-32-px h-32-px bg-danger-50 text-danger-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0 flex-shrink-0"
                                                        onClick={() => handleDeleteBlog(blog.id)}
                                                        disabled={deletingId === blog.id}
                                                    >
                                                        {deletingId === blog.id ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                            <Icon icon="mdi:delete" className="text-lg" />
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-48 bg-neutral-50 radius-12">
                        <Icon icon="mdi:post-outline" className="text-5xl text-secondary-light mb-12" />
                        <p className="text-secondary-light mb-8">No blog posts yet</p>
                        {getRemainingSlots() > 0 && !showAddForm && (
                            <button
                                className="btn btn-primary btn-sm radius-8"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="mdi:plus" className="me-4" />
                                Create First Post
                            </button>
                        )}
                    </div>
                )}

                {/* Image Cropper Modal */}
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                />
            </div>
        </div>
    );
};

export default VenueBlogsManager;
